import { supabase } from '../lib/supabase';
import {
  authCardHtml, mountAuthScreen, fieldHtml, alertHtml, submitHtml, noticeHtml, EMAIL_ATTRS,
  showAlert, clearAlert, clearAlertOnInput, setBusy, markState, bindMarkLight, authErrorCopy,
} from './auth/AuthShell';

// R116 #1: the "Welcome home" shell (pages/auth) — same ids, same Supabase call.
export class ForgotPasswordPage {
  private container: HTMLElement;
  private onBackToLogin: () => void;

  constructor(container: HTMLElement, onBackToLogin: () => void) {
    this.container = container;
    this.onBackToLogin = onBackToLogin;
  }

  async render(): Promise<void> {
    const card = mountAuthScreen(this.container, authCardHtml({
      screen: 'forgot',
      hello: '<b>Reset your password</b>',
      helper: "Enter the email you sign in with and we'll send you a link to choose a new password.",
      body: `
        ${alertHtml('reset-error')}
        <div id="reset-success" hidden></div>
        <form id="reset-form" novalidate>
          ${fieldHtml({ id: 'reset-email', label: 'Email', type: 'email', name: 'email', autocomplete: 'username', placeholder: 'you@company.com', extra: EMAIL_ATTRS })}
          ${submitHtml('reset-submit', 'Send reset link')}
        </form>
        <div class="au-links"><a href="#" id="back-to-login" class="au-link">← Back to sign in</a></div>`,
    }));
    const email = document.getElementById('reset-email') as HTMLInputElement | null;
    email?.focus();
    bindMarkLight(card, [email]);
    this.bindEvents();
  }

  private bindEvents(): void {
    const form = document.getElementById('reset-form');
    const email = document.getElementById('reset-email') as HTMLInputElement | null;
    clearAlertOnInput(document.getElementById('reset-error'), [email]);
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        await this.handleReset();
      });
    }

    const backLink = document.getElementById('back-to-login');
    if (backLink) {
      backLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.onBackToLogin();
      });
    }
  }

  private async handleReset(): Promise<void> {
    const emailEl = document.getElementById('reset-email') as HTMLInputElement;
    const email = emailEl.value.trim();
    const errorDiv = document.getElementById('reset-error');
    const successDiv = document.getElementById('reset-success');
    const submitBtn = document.getElementById('reset-submit') as HTMLButtonElement;
    const card = this.container.querySelector('.au-card') as HTMLElement | null;
    if (submitBtn?.disabled) return;

    if (!email) {
      showAlert(errorDiv, 'Enter your email address.', [emailEl]);
      emailEl.focus();
      return;
    }

    clearAlert(errorDiv, [emailEl]);
    setBusy(submitBtn, true, 'Sending…');
    markState(card, { busy: true });

    let error: any = null;
    try {
      ({ error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`,
      }));
    } catch (e) { error = e; }
    markState(card, { busy: false });

    if (error) {
      showAlert(errorDiv, authErrorCopy(error, 'reset'));
      setBusy(submitBtn, false, 'Send reset link');
    } else {
      // Supabase answers the same whether or not the address has a login, so say exactly that.
      if (successDiv) {
        successDiv.outerHTML = noticeHtml({ tone: 'ok', id: 'reset-success', text: `If ${email} has a login, a reset link is on its way. Check your inbox (and spam).` });
      }
      clearAlert(errorDiv);
      markState(card, { lit: true });
      setBusy(submitBtn, false, 'Send again');
      emailEl.value = '';
    }
  }
}
